/**
 * Control Valve — variable Cv with signal input.
 *
 * Characteristics:
 *   Linear: Cv = Cv_max × opening
 *   Equal percentage: Cv = Cv_max × R^(opening - 1), R = rangeability
 *   Quick opening: Cv = Cv_max × √opening
 *
 * N2 FIX (hydraulic honesty): the derived loss coefficient
 *   K = (Cv_max / Cv)²   (a RELATIVE resistance: K = 1 fully open per this
 *                         normalisation, rising as the valve closes)
 * now drives a flow-dependent, sign-aware pressure drop
 *   ΔP = −K·ρ·u·|u|/2
 * via the shared valveDeltaP() helper, using the solver-supplied throughflow
 * context. Previously K sat in metadata and the valve was hydraulically
 * inert. Without the context (unit tests / direct API use) ΔP stays 0.
 */
import type { NSComponent, Junction, ComponentUpdate } from '../types';
import { valveDeltaP } from './nsValve';

export class NSControlValve implements NSComponent {
  id: string;
  type = 'controlValve';
  params: Record<string, any>;

  constructor(id: string, params: {
    Cv_max?: number;        // Maximum flow coefficient (default 100)
    opening?: number;       // 0-1 valve position (default 0.5)
    characteristic?: 'linear' | 'equalPercent' | 'quickOpen'; // default 'linear'
    rangeability?: number;  // R for equal percentage (default 50)
  }) {
    this.id = id;
    this.params = { Cv_max: 100, opening: 0.5, characteristic: 'linear', rangeability: 50, ...params };
  }

  applyBoundaryConditions(inlet: Junction, _outlet: Junction, _dt: number): ComponentUpdate {
    const { Cv_max, opening, characteristic, rangeability } = this.params;
    const pos = Math.max(0.001, Math.min(1, opening));

    let Cv: number;
    switch (characteristic) {
      case 'equalPercent':
        Cv = Cv_max * Math.pow(rangeability, pos - 1);
        break;
      case 'quickOpen':
        Cv = Cv_max * Math.sqrt(pos);
        break;
      default: // linear
        Cv = Cv_max * pos;
    }

    // K = f(Cv, pipe area) — simplified relative resistance (1 at full open)
    const K = Cv_max > 0 ? Math.pow(Cv_max / Cv, 2) : 1e6;

    // N2: emit the flow-dependent pressure drop (monotonic in opening —
    // smaller Cv ⇒ larger K ⇒ larger drop at the same flow). inlet.P feeds
    // the rail-guard cap (|ΔP| ≤ LOSS_CAP_FRACTION·P_inlet).
    const deltaP = valveDeltaP(this.params, K, undefined, inlet?.P);

    return {
      deltaP,
      deltaH: 0, // Isenthalpic throttling
      momentumSource: 0,
      energySource: 0,
      metadata: { Cv, K, opening: pos, characteristic, deltaP_valve: deltaP },
    };
  }

  setOpening(opening: number): void {
    this.params.opening = Math.max(0, Math.min(1, opening));
  }
}
